import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { useChannelSubscription } from '../composables/useChannelSubscription'
import { apiMutate } from '../composables/apiMutate'
import { apiGet } from '../composables/apiGet'
import {
  applyReviewQueueEvent,
  spliceReviewQueueRow,
  type ReviewQueueChannelPayload,
} from '../composables/reviewQueueChannel'

// Single source for the /synthesis admin review queue. Mirrors
// `useLintNotesStore`'s shape so the two surfaces feel identical to the
// SPA shell — same fetchAll / channel-splice lifecycle, same
// `openCount` sidecar driving the header badge, same Broadcastable
// CRUD vocabulary (`:created` / `:updated`) on the channel payload.
//
// Two notable differences from the lint store:
//
//   1. `update(id, patch)` PATCHes the row's editable columns. Lint has
//      no equivalent — its proposed_patch is a structured triple that
//      gets regenerated server-side; Synthesis's proposed_body is
//      freeform markdown and the SPA Preview→Edit→Accept flow needs to
//      persist edits before triggering accept.
//   2. The list payload includes `source_page_contents` per row (a
//      Hash<path, body>) so the SPA's preview pane can render the
//      source pages alongside the proposed_body without N+1 fetches.

export type SynthesisKind = 'concept_gap' | 'cross_page_pattern' | 'emerging_theme' | 'contradiction_reconciliation'

export interface SynthesisSuggestion {
  id: number
  kind: SynthesisKind
  title: string
  proposed_path: string
  source_paths: string[]
  justification: string
  proposed_body: string
  append_to_existing: boolean
  // Set (== proposed_path) when this suggestion refreshes an existing
  // synthesis page in place: Accept overwrites the page body
  // rather than creating a new file or appending a section. null for
  // create-new / append suggestions.
  supersedes_path: string | null
  status: 'pending' | 'applied' | 'dismissed'
  synthesis_run_id: number | null
  applied_at: string | null
  dismissed_at: string | null
  applied_commit_sha: string | null
  created_at: string
  updated_at: string
  // Present on serialize_with_sources (list + show + channel broadcast).
  // Hash keyed by source path; the value is the page's current on-disk
  // body, or '' if the page has been deleted underneath the suggestion.
  source_page_contents: Record<string, string>
  // Populated for append_to_existing AND supersede rows — the existing
  // page's current body, so the preview pane can show "appending to
  // this" or render a current-vs-proposed diff for a supersede.
  existing_target_content: string
}

interface ListPayload {
  suggestions: SynthesisSuggestion[]
  open_count: number
}

// Channel payload shape is `Broadcastable`'s standard `{event, record,
// open_count}` envelope — see `composables/reviewQueueChannel.ts` for
// the shared splice rules this store and `useLintNotesStore` both run
// against.
type ChannelPayload = ReviewQueueChannelPayload<SynthesisSuggestion>

// Subset of columns the PATCH endpoint accepts. `kind` is deliberately
// not editable — changing it would break the (kind, proposed_path)
// uniqueness contract.
export interface SynthesisSuggestionEdit {
  title?: string
  proposed_path?: string
  proposed_body?: string
  append_to_existing?: boolean
}

export const useSynthesisSuggestionsStore = defineStore('synthesisSuggestions', () => {
  const openCount = ref(0)

  const {
    data: suggestions,
    loaded,
    error: loadError,
    needsAuth,
    refresh: fetchAll,
  } = useAsyncResource<SynthesisSuggestion[]>(
    async () => {
      const res = await fetch(`/api/v1/synthesis_suggestions?status=pending`, { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load synthesis suggestions: ${res.status}`)
      const body: ListPayload = await res.json()
      openCount.value = body.open_count
      return body.suggestions
    },
    { initialValue: [] },
  )

  function fetchOne(id: number): Promise<SynthesisSuggestion | null> {
    return apiGet<SynthesisSuggestion>(`/api/v1/synthesis_suggestions/${id}`, {
      failureLabel: `Failed to load synthesis suggestion ${id}`,
      tolerateStatuses: [404],
    })
  }

  async function update(id: number, patch: SynthesisSuggestionEdit): Promise<SynthesisSuggestion> {
    // SynthesisSuggestionEdit is a typed view onto the four editable
    // columns; apiMutate's body accepts the wider `Record<string, unknown>`.
    // Cast at the boundary so the caller-facing signature stays narrow.
    const updated = await apiMutate<SynthesisSuggestion>(`/api/v1/synthesis_suggestions/${id}`, {
      method: 'PATCH',
      body: patch as Record<string, unknown>,
      failureLabel: 'update failed',
    })
    // Splice the updated row in so the SPA reflects the edit immediately
    // (the channel will also broadcast :updated, but the user clicked
    // Save in the local tab — we don't want them to wait on the round-trip
    // through ActionCable). Channel update later is idempotent.
    const idx = suggestions.value.findIndex((s) => s.id === id)
    if (idx !== -1) suggestions.value[idx] = updated
    return updated
  }

  async function accept(id: number) {
    const result = await mutate(id, 'accept')
    spliceReviewQueueRow(suggestions, openCount, id)
    return result
  }

  async function dismiss(id: number) {
    const result = await mutate(id, 'dismiss')
    spliceReviewQueueRow(suggestions, openCount, id)
    return result
  }

  function mutate(id: number, action: string) {
    return apiMutate(`/api/v1/synthesis_suggestions/${id}/${action}`, {
      method: 'POST',
      failureLabel: `${action} failed`,
    })
  }

  function applyChannelEvent(data: ChannelPayload) {
    applyReviewQueueEvent(data, suggestions, openCount)
  }

  function subscribe() {
    useChannelSubscription<ChannelPayload>('SynthesisSuggestionChannel', {
      received: applyChannelEvent,
      // Re-fetch on reconnect (matches lint store) so events emitted
      // during a transient WS drop don't leave the queue stale.
      // Skip-first is owned by useChannelSubscription.
      refetchOnReconnect: () => void fetchAll(),
    })
  }

  // `!needsAuth` is load-bearing: useAsyncResource sets loaded=true on a
  // 401 too, so without this guard a stale-session 401 (suggestions=[],
  // loadError=null) would read as the benign "all caught up" empty state
  // instead of an auth failure.
  const isEmpty = computed(() => loaded.value && !needsAuth.value && suggestions.value.length === 0)

  return {
    suggestions,
    loaded,
    loadError,
    needsAuth,
    openCount,
    isEmpty,
    fetchAll,
    fetchOne,
    update,
    accept,
    dismiss,
    subscribe,
  }
})
